/**
 * Plain-text search index for the Information Hub.
 *
 * Kept intentionally separate from `pages/Info.jsx` (which owns the rich
 * JSX-with-inline-links rendering of the same content) so we can search
 * over pure strings without pulling `react-dom/server`.
 *
 * When updating a topic's copy in Info.jsx, update the matching entry
 * here so search results stay accurate. If entries drift, the search
 * still works — matches will only surface the pieces mirrored here.
 */

export const INFO_TOPICS = [
  {
    slug: "how-it-works",
    eyebrow: "The basics",
    title: "How MyTableTopApp works",
    intro:
      "Three simple steps — with a parent-approval gate at the centre, so nothing goes live without a grown-up's OK.",
    sections: [
      {
        h: "1. Kids create a listing",
        p: "Toddlers to teens photograph a toy they've outgrown, add a friendly description, and pick a fair price. Younger kids use big buttons and voice-to-text; older kids get a proper listing editor.",
      },
      {
        h: "2. Parents approve",
        p: "Every listing lands in the parent's Moderation Queue. Nothing appears on the public marketplace until you tap Approve — you can edit copy, adjust the price, or send it back with a note.",
      },
      {
        h: "3. Secure checkout & payout",
        p: "Buyers pay via Stripe. A 10% Family Safety & Buyer Protection Fee is added to the buyer's total (funds identity checks, AI moderation, and buyer protection). The full item price is held in the family Wallet until the item ships and the buyer confirms — then payout to your linked bank.",
      },
    ],
  },
  {
    slug: "about",
    eyebrow: "Our story",
    title: "About MyTableTopApp",
    intro:
      "A Hackney Born app that turns Used - Good Condition toys into a first taste of real-world business, finance, and sustainability.",
    sections: [
      {
        h: "Co-designed with real kids",
        p: "We built MyTableTopApp alongside our Year 4 to Year 6 coding club students from Holmleigh, Our Lady, and Shoreditch Park Primary schools. Every screen was shaped by kids telling us what a 'real' shop should look and feel like.",
      },
      {
        h: "Four pillars for young entrepreneurs",
        p: "Financial literacy · e-commerce & marketing · customer service & negotiation · a green initiative that keeps toys out of landfill and money in family pockets.",
      },
      {
        h: "Safety before everything",
        p: "Preset-only messaging, an automated family-safety review on every listing, a PIN-gated Parent Zone, and full UK GDPR (Data Protection) compliance mean parents stay firmly in the loop from listing to payout.",
      },
    ],
  },
  {
    slug: "privacy",
    eyebrow: "Legal",
    title: "Privacy Policy",
    intro:
      "We hold the smallest amount of personal data we possibly can, we never sell it, and we're fully UK GDPR (Data Protection) compliant.",
    sections: [
      {
        h: "What we collect",
        p: "Parent account: email, name, mobile, and hashed password. Kid profile: nickname, age bracket (4–6, 7–10, 11–13, 14–16), and an emoji avatar — never a photo of the child. Listings: photos of the toy itself, description, price. Waitlist: your email + which store you want us to notify you about.",
      },
      {
        h: "How we use it",
        p: "To run the marketplace: authenticate you, show your parent moderation queue, process payments (via Stripe), and notify you about your orders. Analytics are aggregate-only. We do not sell, rent, or share data with advertisers.",
      },
      {
        h: "Payment processing & parent payouts (Stripe Connect)",
        p: "We use Stripe to process wallet top-ups and bank payouts. For identity verification (KYC/AML), bank validation, and payout processing, Stripe operates as an independent Data Controller for personal and financial data. Learn more at stripe.com/privacy.",
      },
      {
        h: "Children's data",
        p: "Under 13s cannot create an account directly — every profile is opened and managed by a verified parent account. We collect no birthdays, no location, no device identifiers beyond a minimal session cookie.",
      },
      {
        h: "Your rights",
        p: "Email hello@mytabletopapp.co.uk to request a copy of your data, correct any of it, or delete your account entirely. We respond within 14 days.",
      },
    ],
  },
  {
    slug: "terms",
    eyebrow: "Legal",
    title: "Terms & Conditions",
    intro: "The house rules for using MyTableTopApp. Plain English, no gotchas.",
    sections: [
      {
        h: "Who can use MyTableTopApp",
        p: "Parent accounts must be 18+ and legally responsible for the child(ren) they add. Kids under 13 use the platform only under a parent account. Sellers must own the item they list and have permission from their parent to sell it.",
      },
      {
        h: "Fees",
        p: "Signing up is free. Sellers keep 100% of the item list price. Buyers pay a 10% Family Safety & Buyer Protection Fee on top of the item price, plus Stripe processing on external card checkouts. Fees are itemised at checkout and in your Wallet. No hidden charges.",
      },
      {
        h: "Payment & payout services",
        p: "Wallet top-ups and parent payouts are executed via Stripe. By using these features, you acknowledge that Stripe handles identity verification, payment processing, and payout management, acting as an independent Data Controller for compliance (KYC/AML) purposes.",
      },
      {
        h: "Prohibited items",
        p: "No food, weapons, digital accounts, live animals, tobacco, alcohol, or anything that would be inappropriate for a child. Our automated safety review and parent-approval queue catch most of this before it goes live.",
      },
      {
        h: "Refunds & disputes",
        p: "Buyers can raise a dispute within 7 days of delivery via the order page. We hold funds in escrow until either side confirms or a resolution is reached.",
      },
      {
        h: "Account termination",
        p: "We may suspend accounts that break these terms. You can delete your account at any time from the Parent Dashboard.",
      },
    ],
  },
  {
    slug: "safety",
    eyebrow: "Trust & safety",
    title: "Parental Safety Guidelines",
    intro:
      "How we keep every child safe on MyTableTopApp — and how you can add your own layer on top.",
    sections: [
      {
        h: "Preset-only messaging",
        p: "Children never type free-form messages to strangers. Communication uses a fixed set of parent-approved presets (\"Is it still available?\", \"Thank you!\", etc.) so bullying, grooming, or personal-detail leaks are structurally impossible.",
      },
      {
        h: "PIN Parent Zone",
        p: "Every sensitive action — approving a listing, editing payout details, changing a kid's age bracket, releasing funds — is gated behind your 4-digit PIN. Set or change it any time at /pin.",
      },
      {
        h: "Automated safety review",
        p: "Every listing photo and description passes through an automated safety review that flags anything unsuitable before you even see it in your Moderation Queue — so parents only spend time on the borderline cases.",
      },
      {
        h: "UK GDPR & Data Protection",
        p: "We never collect a child's real name, birthday, exact location, or contact details. Avatars are emoji-only. Session cookies are the sole client-side identifier.",
      },
      {
        h: "Reporting",
        p: "Something feels off? Email hello@mytabletopapp.co.uk with the listing ID or order ID — we respond within 24 hours, faster for anything that mentions a child's safety.",
      },
    ],
  },
  {
    slug: "faqs",
    eyebrow: "Help",
    title: "Frequently Asked Questions",
    intro:
      "The most common questions from parents and kids getting started with MyTableTopApp.",
    sections: [
      {
        h: "Do I need to install an app?",
        p: "Not yet — the mobile apps are coming to the App Store and Google Play. In the meantime, the full experience runs in your browser at mytabletopapp.co.uk. Tap 'Launch Web App' anywhere on the landing page to jump in.",
      },
      {
        h: "How old does my child need to be?",
        p: "There's an age bracket for every stage: 4–6 (big buttons, visual only), 7–10 and 11–13 (guided listing with parent approval), and 14–16 (optional independent banking with parent override). Everyone under 18 uses a profile inside a parent account.",
      },
      {
        h: "What does it cost?",
        p: "Sign-up is free. When a sale completes, we take £0.30 + 5% plus Stripe processing — itemised at checkout. No listing fees, no subscription.",
      },
      {
        h: "How do payouts work?",
        p: "Sales land in a family Wallet on your Parent Dashboard. Once the buyer confirms delivery (or 7 days pass with no dispute), the funds unlock and you can withdraw to your linked bank via Stripe.",
      },
      {
        h: "Is my child's chat safe?",
        p: "Kids never type free-form messages to buyers. All communication uses parent-approved preset phrases — grooming, bullying, or contact-detail leaks are structurally impossible.",
      },
      {
        h: "Can I remove a listing after it's live?",
        p: "Yes. From your Parent Dashboard's Moderation column, tap any live listing and hit 'Unlist'. It vanishes from the marketplace instantly.",
      },
      {
        h: "Which countries can I sell in?",
        p: "We're UK-only at launch. All prices are in GBP and shipping is domestic Royal Mail / Evri.",
      },
    ],
  },
];


/**
 * Rank-and-return matched sections across every topic.
 *
 * Match rules (all case-insensitive):
 *   - Section heading match  (+30 rank)
 *   - Section paragraph match (+10 rank)
 *   - Topic title match      (+15 rank added to every section of that topic)
 *   - Topic intro match      ( +5 rank added to every section of that topic)
 *
 * Results are sorted highest-rank-first.
 */
export function searchInfoTopics(query) {
  const q = (query || "").trim().toLowerCase();
  if (q.length < 2) return [];

  const results = [];
  for (const topic of INFO_TOPICS) {
    const titleHit = topic.title.toLowerCase().includes(q);
    const introHit = topic.intro.toLowerCase().includes(q);
    for (let i = 0; i < topic.sections.length; i++) {
      const s = topic.sections[i];
      const hHit = s.h.toLowerCase().includes(q);
      const pHit = s.p.toLowerCase().includes(q);
      if (!hHit && !pHit && !titleHit && !introHit) continue;

      let rank = 0;
      if (hHit) rank += 30;
      if (pHit) rank += 10;
      if (titleHit) rank += 15;
      if (introHit) rank += 5;

      results.push({
        topicSlug: topic.slug,
        topicTitle: topic.title,
        topicEyebrow: topic.eyebrow,
        sectionIndex: i,
        h: s.h,
        p: s.p,
        rank,
      });
    }
  }
  results.sort((a, b) => b.rank - a.rank);
  return results;
}


/** Wrap the substrings of `text` matching `query` (case-insensitive) in <mark>. */
export function highlightMatch(text, query) {
  const q = (query || "").trim();
  if (!q || q.length < 2) return text;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const parts = [];
  let cursor = 0;
  let idx = lower.indexOf(needle, cursor);
  while (idx !== -1) {
    if (idx > cursor) parts.push({ t: text.slice(cursor, idx), hit: false });
    parts.push({ t: text.slice(idx, idx + needle.length), hit: true });
    cursor = idx + needle.length;
    idx = lower.indexOf(needle, cursor);
  }
  if (cursor < text.length) parts.push({ t: text.slice(cursor), hit: false });
  return parts;
}
